import { ChevronDown } from 'lucide-react'

import { useAutomations } from '~/app/automations/useAutomations'
import { TableActionTrigger } from '~/components/TableActions'
import { useAccountActionItems } from '~/components/account-detail/AccountActionsMenu/useAccountActionItems'
import { Button, Dropdown, DropdownEntry } from '~/components/component-library'
import { OrgSmartAccount, SmartAccountRole } from '~/types'
import { Automation } from '~/types/automations'

/** Where the menu is rendered — controls the top rows and trigger style */
export type AccountMenuSurface = 'page' | 'table'

type ActionItems = ReturnType<typeof useAccountActionItems>['items']
type MaybeEntry = DropdownEntry | null | undefined

// Drop null (gated) items, then collapse stray dividers
const compactMenuEntries = (entries: MaybeEntry[]): DropdownEntry[] => {
  const collapsed = entries
    .filter((entry): entry is DropdownEntry => entry != null)
    .reduce<DropdownEntry[]>((acc, entry) => {
      const droppedDivider =
        entry === 'divider' &&
        (acc.length === 0 || acc[acc.length - 1] === 'divider')
      return droppedDivider ? acc : [...acc, entry]
    }, [])
  return collapsed[collapsed.length - 1] === 'divider'
    ? collapsed.slice(0, -1)
    : collapsed
}

// Exported for tests
export const buildOperatingItems = (
  actions: ActionItems,
  surface: AccountMenuSurface,
): DropdownEntry[] =>
  compactMenuEntries([
    surface === 'table' ? actions.deposit : null,
    'divider',
    actions.copyAddress,
    actions.connectToApps,
    'divider',
    actions.rename,
    actions.editSigners,
    actions.archive,
    'divider',
    actions.resetSigners,
  ])

export const buildAutomationItems = (
  actions: ActionItems,
  surface: AccountMenuSurface,
): DropdownEntry[] =>
  compactMenuEntries([
    surface === 'table' ? actions.triggerRun : null,
    surface === 'table' ? actions.deposit : null,
    'divider',
    actions.copyAddress,
    actions.showRules,
    'divider',
    // No rename: automations are managed via Edit rules
    actions.editRules,
    actions.archive,
  ])

export const AccountActionsMenu = ({
  smartAccount,
  surface,
  automation: automationProp,
  onConnectToApps,
}: {
  smartAccount: OrgSmartAccount
  surface: AccountMenuSurface
  /** Pass when the caller already has it (table rows) to skip the lookup */
  automation?: Automation
  /** Opens the WalletConnect pairing dialog (page surface only, when available) */
  onConnectToApps?: () => void
}) => {
  const isAutomation = smartAccount.role === SmartAccountRole.AUTOMATION
  const { automations } = useAutomations({
    enabled: isAutomation && automationProp === undefined,
  })
  const automation =
    automationProp ??
    (isAutomation
      ? automations?.find((a) => a.smartAccountId === smartAccount.id)
      : undefined)

  const { items: actions, dialogs } = useAccountActionItems({
    smartAccount,
    automation,
    isPage: surface === 'page',
    onConnectToApps,
  })

  const items = isAutomation
    ? buildAutomationItems(actions, surface)
    : buildOperatingItems(actions, surface)

  return (
    <>
      <Dropdown items={items}>
        {surface === 'table' ? (
          <TableActionTrigger
            aria-label="Account actions"
            onClick={(event) => event.stopPropagation()}
          />
        ) : (
          <Button
            type="button"
            size="small"
            variant="secondary"
            aria-label="Account actions"
            suffix={<ChevronDown />}
          >
            Options
          </Button>
        )}
      </Dropdown>
      {dialogs}
    </>
  )
}
